import { ConnectedPosition, CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
  Component,
  Input,
  effect,
  input,
  model,
  output,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  forwardRef,
  ChangeDetectorRef,
  OnDestroy,
  ElementRef,
  ViewChild,
  ViewChildren,
  QueryList,
  inject
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FormsModule } from '@angular/forms';
import { Observable, Subscription, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '@infinitform/shared-ui';
import { InputComponent } from '../input/input.component';
import { fadeInOnEnterAnimation, slideInUpOnEnterAnimation } from 'angular-animations';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ],
  imports: [CdkOverlayOrigin, CdkConnectedOverlay, FormsModule, ButtonComponent, InputComponent],
  animations: [
    fadeInOnEnterAnimation({ duration: 150 }),
    slideInUpOnEnterAnimation({ duration: 150, translate: '10px' })
  ]
})
export class SelectComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  type = input<'custom-dropdown' | 'default'>('default');

  options = input<unknown[] | Observable<unknown[]> | null>([]);
  asyncOptions = input<Observable<unknown[]> | null>(null);
  private _selected: unknown | unknown[] = null;
  @Input() disabled = false;
  multi = input(false);
  bindValue = input<string | undefined>(undefined);
  bindLabel = input<string | undefined>(undefined);
  bindBadge = input<string | undefined>(undefined);
  loading = model(false);
  placeholder = input('Select an option');
  searchable = input(false);
  searchPlaceholder = input('Search...');
  minSearchLength = input(0);
  searchDebounceTime = input(300, { alias: 'debounceTime' });
  clearable = input(false);
  change = output<unknown>();
  search = output<string>();
  size = input<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'text'>('md');

  @ViewChild('trigger') originElement!: ElementRef;
  @ViewChild('searchInput', { static: false }) searchInput!: InputComponent;
  @ViewChild('optionsContainer') optionsContainer?: ElementRef<HTMLElement>;
  @ViewChildren('optionItem') optionItems?: QueryList<ElementRef<HTMLElement>>;
  get placeholderVisible(): boolean {
    if (this.multi()) {
      return !this._selected || (this._selected as unknown[]).length === 0;
    }
    return !this._selected;
  }

  private _isOpen: boolean = false;
  private currentValue: any = null;
  private optionsSubscription: Subscription | null = null;
  private _options: any[] = [];
  private _filteredOptions: any[] = [];
  private searchSubject = new Subject<string>();
  searchTerm = '';
  highlightedIndex = -1;
  searchValue = '';

  constructor() {
    // Set up search subscription with debouncing
    this.searchSubject
      .pipe(debounceTime(this.searchDebounceTime()), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((searchTerm) => {
        this.performSearch(searchTerm);
      });

    // React to signal input changes (replaces ngOnChanges for options/asyncOptions)
    effect(() => {
      this.options();
      this.handleOptionsChange();
    });
    effect(() => {
      this.asyncOptions();
      this.handleAsyncOptionsChange();
    });
  }

  originWidth = 0;

  private updateOriginWidth(): void {
    this.originWidth = this.originElement?.nativeElement?.offsetWidth || 0;
  }

  get selected(): any | any[] {
    return this._selected;
  }

  get currentOptions(): Record<string, unknown>[] {
    return this.searchable() ? this._filteredOptions : this._options;
  }

  /**
   * Get available (unlocked) options
   */
  get availableOptions(): any[] {
    return this.currentOptions.filter((opt) => !opt?.['locked']);
  }

  /**
   * Get locked options (not available in subscription)
   */
  get lockedOptions(): any[] {
    return this.currentOptions.filter((opt) => opt?.['locked'] === true);
  }

  /**
   * Check if there are any locked options to show the restricted group
   */
  get hasLockedOptions(): boolean {
    return this.lockedOptions.length > 0;
  }

  get isSearching(): boolean {
    return this.searchable() && this.searchTerm.length >= this.minSearchLength();
  }

  set selected(value: any | any[]) {
    this._selected = value;
    this.cdr.detectChanges();
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    this._isOpen = value;
    this.cdr.detectChanges();
  }

  get selectedLabel(): string {
    if (this.multi()) {
      if (!this.selected || (this.selected as unknown[]).length === 0) {
        return this.placeholder();
      }
      if ((this.selected as unknown[]).length === 1) {
        const label = this.bindLabel();
        return label ? (this.selected as Record<string, string>[])[0][label] : (this.selected as unknown[])[0] as string;
      }
      return `${(this.selected as unknown[]).length} items selected`;
    }

    const selected = this.selected;
    if (!selected) {
      return this.placeholder();
    }
    const label = this.bindLabel();
    return label ? (selected as Record<string, string>)[label] : selected as string;
  }

  get selectedBadge(): string | undefined {
    if (this.multi() || !this.selected || !this.bindBadge()) return undefined;
    return (this.selected as Record<string, string>)[this.bindBadge()!] || undefined;
  }

  // ControlValueAccessor implementation
  private onChange = (value: any) => {};
  private onTouched = () => {};

  writeValue(value: any): void {
    this.currentValue = value;

    if (value == null) {
      this.selected = this.multi() ? [] : null;
      return;
    }

    if (this.bindValue()) {
      if (this.multi()) {
        if (Array.isArray(value)) {
          this.selected = this._options.filter((opt: Record<string, unknown>) => value.includes(opt[this.bindValue()!]));
        } else {
          this.selected = [];
        }
      } else {
        const option = this._options.find((opt: Record<string, unknown>) => opt[this.bindValue()!] === value);
        this.selected = option || null;
      }
    } else {
      this.selected = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  isSelected(option: Record<string, unknown>): boolean {
    if (this.multi()) {
      return this.selected && (this.selected as unknown[]).some((item: unknown) => item === option);
    }
    const selected = this.selected;
    if (!selected) return false;
    const bv = this.bindValue();
    if (bv) {
      if (bv !== 'value') {
        return (selected as Record<string, unknown>)[bv] === option[bv];
      }
      return (selected as Record<string, unknown>)['value'] === option['value'];
    }
    return option === selected;
  }

  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top'
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top'
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom'
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom'
    }
  ];

  toggleDropdown() {
    // don't open/close if disabled
    if (this.disabled) return;

    if (!this._isOpen) {
      this.updateOriginWidth();
    }
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.onDropdownOpen();
    } else {
      this.clearSearch();
    }
  }

  /**
   * Check if an individual option is disabled
   */
  isOptionDisabled(option: any): boolean {
    return option?.disabled === true || option?.locked === true;
  }

  selectOption(option: unknown) {
    if (this.disabled || this.isOptionDisabled(option)) return;

    if (this.multi()) {
      const selectedItems = (this.selected as unknown[]) || [];
      const index = selectedItems.findIndex((item: unknown) => item === option);
      if (index > -1) {
        selectedItems.splice(index, 1);
      } else {
        selectedItems.push(option);
      }
      this.selected = [...selectedItems];
      const bv = this.bindValue();
      const values = (this.selected as Record<string, unknown>[]).map((item) => (bv ? item[bv] : item));
      this.currentValue = values;
      this.onChange(values);
      this.change.emit(values);
    } else {
      const bv = this.bindValue();
      const value = bv ? (option as Record<string, unknown>)[bv] : option;

      this.selected = option;
      this.currentValue = value;
      this.onChange(value);
      this.onTouched();
      this.change.emit(value);
      this.isOpen = false;
    }

    if (this.searchable()) {
      this.clearSearch();
    }
  }

  clearSelection(event: Event) {
    event.stopPropagation();
    if (this.multi()) {
      this.selected = [];
      this.onChange([]);
      this.change.emit([]);
    } else if (this.clearable()) {
      this.selected = null;
      this.onChange(null);
      this.change.emit(null);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled'] && this.disabled) {
      // ensure overlay is closed when component becomes disabled
      if (this.isOpen) this.isOpen = false;
    }
  }

  ngOnDestroy() {
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }
  }

  private handleOptionsChange(): void {
    const opts = this.options();
    if (Array.isArray(opts)) {
      this._options = opts;
      this._filteredOptions = [...this._options];
      this.updateSelectedFromCurrentValue();
    } else if (opts instanceof Observable) {
      this.subscribeToOptions(opts);
    }
  }

  private handleAsyncOptionsChange(): void {
    const asyncOpts = this.asyncOptions();
    if (asyncOpts) {
      this.subscribeToOptions(asyncOpts);
    }
  }

  private subscribeToOptions(observable: Observable<any[]>) {
    // Unsubscribe from previous subscription
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }

    this.loading.set(true);
    this.optionsSubscription = observable.subscribe({
      next: (options) => {
        this._options = options;
        this._filteredOptions = [...this._options];
        this.loading.set(false);
        this.updateSelectedFromCurrentValue();
        this.cdr.detectChanges();
        if (this.isOpen) {
          this.queueScrollSelectedIntoView();
        }
      },
      error: (error) => {
        console.error('Error loading options:', error);
        this.loading.set(false);
        this._options = [];
        this._filteredOptions = [];
        this.cdr.detectChanges();
      }
    });
  }

  private updateSelectedFromCurrentValue(): void {
    const bv = this.bindValue();
    if (this.currentValue != null && bv && this._options.length > 0) {
      if (this.multi()) {
        if (Array.isArray(this.currentValue)) {
          this.selected = this._options.filter((opt: Record<string, unknown>) => this.currentValue.includes(opt[bv]));
        }
      } else {
        const option = this._options.find((opt: Record<string, unknown>) => opt[bv] === this.currentValue);
        this.selected = option || null;
      }
    }
  }

  // Search methods
  onSearchValueChange() {
    this.searchTerm = this.searchValue;
    this.highlightedIndex = -1;

    if (this.searchTerm.length >= this.minSearchLength()) {
      this.searchSubject.next(this.searchTerm);
    } else {
      this._filteredOptions = [...this._options];
      this.cdr.detectChanges();
    }
  }

  onSearchKeyDown(event: KeyboardEvent) {
    if (!this.searchable()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;
      case 'Enter':
        event.preventDefault();
        this.selectHighlighted();
        break;
      case 'Escape':
        event.preventDefault();
        this.isOpen = false;
        break;
    }
  }

  private performSearch(searchTerm: string): void {
    if (!this.searchable()) return;

    this.search.emit(searchTerm);

    if (searchTerm.length < this.minSearchLength()) {
      this._filteredOptions = [...this._options];
    } else {
      this._filteredOptions = this._options.filter((option) => {
        const bl = this.bindLabel();
        const searchText = bl ? (option as Record<string, string>)[bl] : option.toString();
        return searchText.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    this.highlightedIndex = -1;
    this.cdr.detectChanges();
  }

  private highlightNext() {
    if (this._filteredOptions.length === 0) return;
    this.highlightedIndex = (this.highlightedIndex + 1) % this._filteredOptions.length;
    this.cdr.detectChanges();
  }

  private highlightPrevious() {
    if (this._filteredOptions.length === 0) return;
    this.highlightedIndex = this.highlightedIndex <= 0 ? this._filteredOptions.length - 1 : this.highlightedIndex - 1;
    this.cdr.detectChanges();
  }

  private selectHighlighted() {
    if (this.highlightedIndex >= 0 && this.highlightedIndex < this._filteredOptions.length) {
      this.selectOption(this._filteredOptions[this.highlightedIndex]);
    }
  }

  clearSearch() {
    this.searchTerm = '';
    this.searchValue = '';
    this.highlightedIndex = -1;
    this._filteredOptions = [...this._options];
    this.cdr.detectChanges();
  }

  isHighlighted(option: unknown): boolean {
    if (!this.searchable()) return false;
    const index = this._filteredOptions.findIndex((opt) => opt === option);
    return index === this.highlightedIndex;
  }

  onDropdownOpen() {
    if (this.searchable() && this.searchInput) {
      // Focus search input when dropdown opens
      setTimeout(() => {
        const inputElement = (this.searchInput as any).elementRef?.nativeElement?.querySelector('input');
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }

    this.queueScrollSelectedIntoView();
  }

  private queueScrollSelectedIntoView() {
    // Defer until the view updates to ensure option elements are present
    setTimeout(() => {
      if (!this.isOpen) return;
      this.scrollSelectedIntoView();
    }, 0);
  }

  private scrollSelectedIntoView() {
    const container = this.optionsContainer?.nativeElement;
    const optionElements = this.optionItems?.toArray() ?? [];
    if (!container || optionElements.length === 0) return;

    const targetIndex = this.getFirstSelectedIndex();
    if (targetIndex < 0 || targetIndex >= optionElements.length) return;

    const targetElement = optionElements[targetIndex].nativeElement;
    if (!targetElement) return;

    if (typeof targetElement.scrollIntoView === 'function') {
      targetElement.scrollIntoView({ block: 'nearest' });
    } else {
      const { offsetTop, offsetHeight } = targetElement;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const offsetBottom = offsetTop + offsetHeight;

      if (offsetTop < scrollTop) {
        container.scrollTop = offsetTop;
      } else if (offsetBottom > scrollTop + containerHeight) {
        container.scrollTop = offsetBottom - containerHeight;
      }
    }
  }

  private getFirstSelectedIndex(): number {
    const options = this.currentOptions;
    if (!options || options.length === 0) return -1;

    if (this.multi()) {
      return options.findIndex((option) => this.isSelected(option));
    }

    const selected = this.selected;
    if (!selected) return -1;

    if (this.bindValue()) {
      return options.findIndex((option) => option['value'] === (selected as Record<string, unknown>)['value']);
    }

    return options.findIndex((option) => option === selected);
  }
}
